import * as Updates from 'expo-updates';
import BottomSheet from '@gorhom/bottom-sheet';
import i18n from '@/utils/i18n';
import {
  adaptVariantPackMetadata,
  useAdaptVariantPackToItemListOption,
} from '@/contexts/item-context-utils';
import { Analytics } from '@/utils/analytics';
import { ApolloError, useMutation } from '@apollo/client';
import { captureException } from '@/utils/sentry';
import {
  defaultDynamicConfigValues,
  getValueFromConfigs,
} from '@/contexts/dynamic-config-utils';
import { EmployeeRestaurant, useUserContext } from '@/contexts/user-context';
import { getLatestOpenSmartOrderUUID } from '@/screens/order-inbounds/utils';
import { getMessageFromGQLError } from '@/utils/apollo-client';
import {
  getUpdatedOrder,
  useNewOrderActionsContext,
} from '@/contexts/new-order-actions-context';
import {
  InboundListItem,
  OrderMetadata,
  UnknownContactInfo,
  VariantPackOrderCountsMap,
} from '@/screens/order-inbounds/types';
import { InteractionManager, Platform } from 'react-native';
import { isNotMaliPhoneNumber } from '@/utils/phone-number';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { NEW_ORDER_ROUTES, ORDER_INBOUNDS_ROUTES } from '@/navigators/routes';
import { OrderInboundsStackParams } from '@/navigators/types';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import { QuantityMapType } from '@/screens/new-order/hooks/use-quantity-reducer';
import {
  RefetchQueryType,
  RegenerateSmartOrderParams,
} from './use-regenerate-smart-order';
import {
  RestaurantEmployeeFragment,
  SmartOrderSearchInputsFieldsFragment,
  UpdateEmployee_UpdateMutation,
  UpdateEmployee_UpdateMutationVariables,
} from '@/types';
import { SmartOrderSearchTermStatus, SmartOrderStatus } from '@/enums';
import { StackNavigationProp } from '@react-navigation/stack';
import { Type } from '@/components/toast';
import {
  UPDATE_EMPLOYEE_MUTATION,
  UPDATE_EMPLOYEE_OVERWRITE_CONTEXT,
} from '@/screens/update-employee-screen';
import {
  useBaseItemQueryVariables,
  useItemQueryLocale,
} from './use-item-query-variables';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  useCreateSearchTermCorrection,
  useMemorizeOrder,
  useUpdateSmartOrderItem,
} from '@/screens/order-inbounds/queries';
import { useGetAddToOrderDisabled } from './use-add-to-order-disabled';
import { useInboundReviewTiming } from './use-inbound-review-timing';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOrderDrawerContext } from '@/contexts/order-drawer-context';
import { useOrderInboundDetailStore } from '@/screens/order-inbounds/store/order-inbound-detail-store';
import { useOrderInboundReducer } from './use-order-inbound-reducer';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';
import { usePillNav } from '@/screens/order-inbounds/hooks/use-pill-nav';
import { UserOverridesContext } from '@/contexts/user-overrides-context';
import {
  useSupplierDynamicConfigFlag,
  useSupplierDynamicConfigValue,
} from './use-dynamic-config-value';
import { useToasts } from '@/contexts/toast-context';
import type { InboundDetailListOrderOptionsRef } from '@/screens/order-inbounds/inbound-detail-list-order-options';

export const LIST_ITEM_GAP = 24;

const ITEM_NOTE_SEPARATOR = '; ';

export const joinItemNotes = (notes: string[], maxLength?: number): string => {
  const joined = notes.join(ITEM_NOTE_SEPARATOR);
  return maxLength ? joined.slice(0, maxLength) : joined;
};

export interface AssignSmartOrderParams {
  employeeRestaurant: EmployeeRestaurant;
  employeeUUID: string;
  skipLoadingState?: boolean;
}
export type AssignSmartOrderFunction = (
  params: AssignSmartOrderParams,
) => Promise<void>;

interface Props {
  activeRestaurant: EmployeeRestaurant | null;
  assignSmartOrder: AssignSmartOrderFunction;
  isProcessing: boolean;
  isViewOnly?: boolean;
  navigation: StackNavigationProp<
    OrderInboundsStackParams,
    ORDER_INBOUNDS_ROUTES.Submit
  >;
  onRegenerateSmartOrder?: (orderUUID: string, restaurantUUID: string) => void;
  orderMetadata?: OrderMetadata[];
  orderOptionsRef?: React.RefObject<InboundDetailListOrderOptionsRef | null>;
  orderUUID: string;
  refetchOrderDetails: () => void;
  refetchSearchTermCorrections: () => void;
  regenerateSmartOrders?: (
    params: RegenerateSmartOrderParams,
    refetchQueryType?: RefetchQueryType,
  ) => Promise<void>;
  savedVariantPackOrderCountsMap: VariantPackOrderCountsMap;
  selectNextSmartOrder: (smartOrderUUID: string) => void;
  unknownContactInfo?: UnknownContactInfo;
}

export const useOrderInboundsDetail = ({
  assignSmartOrder,
  navigation,
  activeRestaurant,
  isProcessing,
  regenerateSmartOrders,
  orderMetadata,
  orderOptionsRef,
  orderUUID,
  onRegenerateSmartOrder,
  refetchOrderDetails,
  refetchSearchTermCorrections,
  unknownContactInfo,
  selectNextSmartOrder,
  isViewOnly,
  savedVariantPackOrderCountsMap,
}: Props) => {
  const { goBack, navigate } = navigation;
  const setShowAddItemSheet = useOrderInboundDetailStore(
    (state) => state.actions.setShowAddItemSheet,
  );
  const setUpdatingLocation = useOrderInboundDetailStore(
    (state) => state.actions.setUpdatingLocation,
  );
  const pendingQuantityMap = useOrderInboundDetailStore(
    (state) => state.pendingQuantityMap,
  );
  const [continueButtonLoading, setContinueButtonLoading] = useState(false);
  const [assigningRestaurant, setAssigningRestaurant] =
    useState<EmployeeRestaurant>();
  const [
    swappedVariantPackOrderCountsMap,
    setSwappedVariantPackOrderCountsMap,
  ] = useState<VariantPackOrderCountsMap>(new Map());
  const sheetRef = useRef<BottomSheet>(null);
  const { showErrorToast, showToast } = useToasts();

  const addSmartOrdersToSubmitQueue = useOrderInboundsStore(
    (state) => state.actions.addSmartOrdersToSubmitQueue,
  );

  const setIsSwapInProgress = useOrderInboundsStore(
    (state) => state.actions.setIsSwapInProgress,
  );
  const setIsCorrectionInProgress = useOrderInboundsStore(
    (state) => state.actions.setIsCorrectionInProgress,
  );

  const overrideValues = useContext(UserOverridesContext);
  const { openDrawer, setDrawerState } = useOrderDrawerContext();
  const { replaceOrderQuantities } = useNewOrderActionsContext();
  const { draftOrder } = useNewOrderContext();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();

  const [updateSmartOrderItem] = useUpdateSmartOrderItem();
  const [createSearchTermCorrection] = useCreateSearchTermCorrection();
  const [memorizeOrder, { loading: isMemorizingOrder }] = useMemorizeOrder();
  const baseItemQueryVariables = useBaseItemQueryVariables();
  const [orderInboundState, dispatch] = useOrderInboundReducer();
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const itemNotesMaxLength = useSupplierDynamicConfigValue(
    'line_item_notes_config',
  )?.max_length;
  const isInboundHistoryEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'inbound_history_enabled',
  );
  const pillNavProps = usePillNav({
    isInboundHistoryEnabled,
    itemGap: LIST_ITEM_GAP,
    matchTerms: orderInboundState.matchTerms,
  });
  const getAddToOrderDisabled = useGetAddToOrderDisabled();

  // Adapt any variant packs into a list item to ensure they update if any
  // promotion or availability data updates once items are loaded
  const detailListItems = useMemo(
    () =>
      orderInboundState.listItems.map<InboundListItem>((item) => {
        const metadata = item.metadata!;
        if (metadata.variantPack) {
          const orderCounts =
            swappedVariantPackOrderCountsMap.get(metadata.variantPack.uuid) ??
            savedVariantPackOrderCountsMap.get(metadata.variantPack.uuid);
          const adapted = adaptVariantPackMetadata(
            adaptVariantPackToItemListOption(
              metadata.variantPack,
              undefined,
              orderCounts,
            ),
          );
          return {
            ...item,
            metadata: {
              inputWithResult: metadata.inputWithResult,
              listOption: adapted,
              matchTerm: metadata.matchTerm,
            },
          };
        }
        return item;
      }),
    [
      adaptVariantPackToItemListOption,
      orderInboundState.listItems,
      swappedVariantPackOrderCountsMap,
      savedVariantPackOrderCountsMap,
    ],
  );

  const navigateToAdd = () => {
    setShowAddItemSheet(true);
    if (isSmallLayout) {
      navigation.navigate(ORDER_INBOUNDS_ROUTES.AddSearch, {
        dispatch,
        onClose: (hasItems) => {
          setShowAddItemSheet(false);
          navigation.goBack();
          if (hasItems && pillNavProps.listRef.current) {
            const ref = pillNavProps.listRef.current;
            InteractionManager.runAfterInteractions(() =>
              ref.scrollToEnd({ animated: true }),
            );
          }
        },
        smartOrderUUID: addItemSmartOrderUUID,
      });
    }
  };
  const [updateEmployee] = useMutation<
    UpdateEmployee_UpdateMutation,
    UpdateEmployee_UpdateMutationVariables
  >(UPDATE_EMPLOYEE_MUTATION, {
    context: UPDATE_EMPLOYEE_OVERWRITE_CONTEXT,
  });

  const { phoneNumber, emailAddress } = unknownContactInfo ?? {};

  const orderUUIDs = useMemo(() => {
    if (orderMetadata) {
      return orderMetadata.map((metaData) => {
        return metaData.smartOrderUUID;
      });
    }
    return [orderUUID];
  }, [orderMetadata, orderUUID]);

  // A merged/threaded orderUUIDs can hold already-SUBMITTED/REJECTED entries
  // from earlier in the thread alongside the one(s) actually being acted on.
  const pendingOrderUUIDs = useMemo(() => {
    if (orderMetadata) {
      return orderMetadata
        .filter((metaData) => metaData.status === SmartOrderStatus.PENDING)
        .map((metaData) => metaData.smartOrderUUID);
    }
    return [orderUUID];
  }, [orderMetadata, orderUUID]);

  const addItemSmartOrderUUID = useMemo(
    () =>
      (orderMetadata && getLatestOpenSmartOrderUUID(orderMetadata)) ??
      orderUUID,
    [orderMetadata, orderUUID],
  );

  const { recordInteraction, stageContinuePress } = useInboundReviewTiming({
    isProcessing,
    navigation,
    orderUUID,
    orderUUIDs,
    pendingOrderUUIDs,
    restaurantUUID: activeRestaurant?.uuid,
    supplierUUID: activeRestaurant?.supplierUUID,
  });

  const onRemoveItem = (removedItem: InboundListItem) => {
    const searchInputUUID = removedItem.metadata!.inputWithResult.uuid;
    dispatch({
      payload: { searchInputUUIDs: [searchInputUUID] },
      type: 'REMOVE_ITEMS',
    });
  };

  const onSwapItem = async (
    previousItem: InboundListItem,
    itemMetadata: ItemListOptionMetadata,
    onComplete: () => void = () => {},
    rememberSelection = true,
  ) => {
    setIsSwapInProgress(true);

    const originalSearchInput = previousItem.metadata?.inputWithResult.result;

    Analytics.track('order_inbound_detail.swap_assigned_item', {
      chatUUID: activeRestaurant?.chatUUIDs[0],
      nextVariantPackUUID: itemMetadata.variantPackUUID,
      previousVariantPackUUID:
        previousItem.metadata?.listOption?.metadata?.variantPackUUID,
      restaurantUUID: activeRestaurant?.uuid,
      searchTerm: previousItem.title,
      smartOrderSearchInputUUID: originalSearchInput?.uuid,
      smartOrderUUID: originalSearchInput?.smart_order_uuid,
    });

    const counts = itemMetadata.variantPackOrderCounts;
    if (counts) {
      const variantPackUUID = itemMetadata.variantPackUUID;
      setSwappedVariantPackOrderCountsMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(variantPackUUID, counts);
        return newMap;
      });
    }

    if (!activeRestaurant || !originalSearchInput) {
      dispatch({
        payload: {
          itemMetadata,
          previousItem,
        },
        type: 'SWAP_ITEM',
      });
      setIsSwapInProgress(false);
      onComplete();
      return;
    }

    let updatedSearchInput: SmartOrderSearchInputsFieldsFragment | undefined;
    try {
      const updateResult = await updateSmartOrderItem({
        variables: {
          ...baseItemQueryVariables,
          newSearchTerm: originalSearchInput.search_term.term,
          newVariantPackUUID: itemMetadata.variantPackUUID,
          recalculateQuantity: true,
          rememberSelection,
          restaurantUUID: activeRestaurant.uuid,
          skipGroupMemberships: true,
          smartOrderSearchInputUUID: originalSearchInput.uuid,
          supplierUUID: activeRestaurant.supplierUUID,
        },
      });

      const responseSearchInput =
        updateResult.data?.updateSearchInput.smart_order_search_input;

      if (responseSearchInput) {
        updatedSearchInput = responseSearchInput;
        refetchOrderDetails();
      }
    } catch (e) {
      showErrorToast();
    } finally {
      setIsSwapInProgress(false);
    }

    onComplete();

    dispatch({
      payload: {
        itemMetadata,
        previousItem,
        searchInput: updatedSearchInput,
      },
      type: 'SWAP_ITEM',
    });
    sheetRef.current?.snapToIndex(1);
  };

  const onItemCorrection = async (
    item: InboundListItem,
    itemMetadata: ItemListOptionMetadata,
    onComplete: () => void = () => {},
  ) => {
    const originalSearchInput = item.metadata?.inputWithResult.result;

    if (!activeRestaurant || !originalSearchInput) {
      onComplete();
      return;
    }

    setIsCorrectionInProgress(true);

    Analytics.track('order_inbound_detail.correct_assigned_item', {
      chatUUID: activeRestaurant.chatUUIDs[0],
      nextVariantPackUUID: itemMetadata.variantPackUUID,
      restaurantUUID: activeRestaurant.uuid,
      searchTerm: item.title,
      smartOrderSearchTermUUID: originalSearchInput.search_term.uuid,
      smartOrderUUID: originalSearchInput.smart_order_uuid,
    });

    try {
      await createSearchTermCorrection({
        variables: {
          oldSearchTermUUID: originalSearchInput.search_term.uuid,
          restaurantUUID: activeRestaurant.uuid,
          smartOrderUUID: originalSearchInput.smart_order_uuid,
          supplierUUID: activeRestaurant.supplierUUID,
          variantPackUUID: itemMetadata.variantPackUUID,
        },
      });
      refetchSearchTermCorrections();
    } catch (e) {
      showErrorToast();
    } finally {
      setIsCorrectionInProgress(false);
    }

    onComplete();
  };

  const onSetRememberSelection = async (
    item: InboundListItem,
    nextValue: boolean,
  ) => {
    const originalMetadata = item.metadata;
    const originalSearchInput = originalMetadata?.inputWithResult.result;
    const listItemOptionMetadata = originalMetadata?.listOption?.metadata;
    const nextVariantPackUUID = listItemOptionMetadata?.variantPackUUID;

    if (
      !activeRestaurant ||
      !originalSearchInput ||
      !originalMetadata ||
      !listItemOptionMetadata ||
      !nextVariantPackUUID
    ) {
      return;
    }

    const status = nextValue
      ? SmartOrderSearchTermStatus.DRAFT
      : SmartOrderSearchTermStatus.NO_REUSE;

    // Update state optimistically and reconcile later
    dispatch({
      payload: {
        itemUUID: item.value,
        status,
      },
      type: 'SET_ITEM_STATUS',
    });

    Analytics.track('order_inbound_detail.toggle_remember_selection', {
      chatUUID: activeRestaurant.chatUUIDs[0],
      nextVariantPackUUID,
      previousVariantPackUUID: nextVariantPackUUID,
      rememberSelection: nextValue,
      restaurantUUID: activeRestaurant.uuid,
      searchTerm: item.title,
      smartOrderSearchInputUUID: originalSearchInput.uuid,
      smartOrderUUID: originalSearchInput.smart_order_uuid,
    });

    try {
      await updateSmartOrderItem({
        variables: {
          ...baseItemQueryVariables,
          newSearchTerm: originalMetadata.inputWithResult.searchTerm ?? '',
          newVariantPackUUID: nextVariantPackUUID,
          recalculateQuantity: false,
          rememberSelection: nextValue,
          restaurantUUID: activeRestaurant.uuid,
          skipGroupMemberships: true,
          smartOrderSearchInputUUID: originalSearchInput.uuid,
          supplierUUID: activeRestaurant.supplierUUID,
        },
      });
    } catch (e) {
      // Revert the state if the request failed
      dispatch({
        payload: {
          itemUUID: item.value,
          status: originalMetadata.inputWithResult.status,
        },
        type: 'SET_ITEM_STATUS',
      });
      showErrorToast();
    }
  };

  // HACK: prevent navigating to place the order if we do not have
  // an override, or the override restaurant does not match the restaurant
  // assigned to the order
  const reloadIfInvalidOverrideRestaurant = useCallback(() => {
    const activeRestaurantUUID = activeRestaurant?.uuid;
    const overrideRestaurantUUID =
      'selectedRestaurantUUID' in overrideValues
        ? overrideValues.selectedRestaurantUUID
        : null;

    const isOverrideRestaurantValid =
      activeRestaurantUUID &&
      overrideRestaurantUUID &&
      activeRestaurantUUID === overrideRestaurantUUID;
    if (!isOverrideRestaurantValid) {
      Analytics.track('order_inbounds_detail.invalid_override_restaurant', {
        activeRestaurantUUID,
        overrideRestaurantUUID,
        smartOrderUUID: orderUUIDs[0],
      });
      showErrorToast();
      captureException(new Error('invalid smart order override restaurant'), {
        extra: {
          activeRestaurantUUID,
          overrideRestaurantUUID,
          smartOrderUUID: orderUUIDs[0],
        },
      });

      if (Platform.OS === 'web') {
        location.reload();
      } else {
        Updates.reloadAsync();
      }
      return false;
    }
    return true;
  }, [activeRestaurant?.uuid, overrideValues, showErrorToast, orderUUIDs]);

  const onContinuePress = async () => {
    // Captured synchronously so this reflects the actual click, not
    // whenever the async validation/cart-update below finishes.
    const continuePressedAt = performance.now();

    if (!reloadIfInvalidOverrideRestaurant()) {
      return;
    }
    try {
      setContinueButtonLoading(true);

      PerfTiming.start(PerfTimingEvent.ORDER_SUMMARY_VISIBLE, {
        entryPoint: 'order_inbounds_detail_continue_button',
      });

      // Validate PO number before continuing
      if (orderOptionsRef?.current) {
        const isValid = orderOptionsRef.current.validateAndFocus();
        if (!isValid) {
          orderOptionsRef.current.scrollToSelf();
          setContinueButtonLoading(false);
          return;
        }
      }

      // Zeroes for everything this press adds, so backing out of the summary
      // screen can undo it
      const quantitiesToUndo: QuantityMapType = {};

      // If the user clicks continue before debounce saves the search input,
      // we won't have a list item in the reducer yet. Manually add these items
      // to the order
      const unsavedQuantities = Array.from(
        pendingQuantityMap.entries(),
      ).reduce<QuantityMapType>((acc, [variantPackUUID, value]) => {
        if (value.quantity !== 0 && !value.searchInputUUID) {
          acc[variantPackUUID] = value.quantity;
          quantitiesToUndo[variantPackUUID] = 0;
        }
        return acc;
      }, {});

      const itemNotesByVariantPack: Record<string, string[]> = {};
      const pendingItemNotes =
        useOrderInboundDetailStore.getState().pendingItemNotes;

      // Add top-down so added_at ordering matches the inbound list. The
      // summary screen sorts smart orders ascending to align with this,
      // the ERP export, and the order detail view.
      const smartOrderQuantities =
        orderInboundState.listItems.reduce<QuantityMapType>((acc, item) => {
          const { itemNotes: committedItemNotes, uuid: searchInputUUID } =
            item.metadata!.inputWithResult;
          const itemNotes =
            pendingItemNotes.get(searchInputUUID) ?? committedItemNotes;
          const { variantPackUUID, quantity } =
            orderInboundState.inputToQuantityMap[searchInputUUID] || {};
          if (variantPackUUID && typeof quantity === 'number') {
            if (getAddToOrderDisabled(variantPackUUID)) {
              return acc;
            }
            // if multiple terms map to the same VP, combine quantities
            const existing = acc[variantPackUUID] || 0;
            acc[variantPackUUID] = existing + quantity;
            quantitiesToUndo[variantPackUUID] = 0;

            const trimmedNote = itemNotes?.trim();
            if (trimmedNote) {
              itemNotesByVariantPack[variantPackUUID] = [
                ...(itemNotesByVariantPack[variantPackUUID] ?? []),
                trimmedNote,
              ];
            }
          }
          return acc;
        }, unsavedQuantities);

      const itemNotes = Object.entries(itemNotesByVariantPack).reduce<
        Record<string, string>
      >((acc, [variantPackUUID, notes]) => {
        if (smartOrderQuantities[variantPackUUID] > 0) {
          acc[variantPackUUID] = joinItemNotes(notes, itemNotesMaxLength);
        }
        return acc;
      }, {});

      // This smart order is the entire order. Completely replace draft order
      // quantities or any lingering unsaved quantities.
      const result = await replaceOrderQuantities(
        smartOrderQuantities,
        'order_inbound_detail',
        { itemNotes },
      );
      const updatedOrder = getUpdatedOrder(result);

      if (!updatedOrder) {
        Analytics.track('smart_order.update_cart_failed', {
          draftOrderUUID: draftOrder?.uuid,
          smartOrderQuantities,
          smartOrderUUIDs: orderUUIDs,
        });

        captureException(new Error('smart order update cart failed'), {
          extra: {
            draftOrderUUID: draftOrder?.uuid,
            smartOrderQuantities,
            smartOrderUUIDs: orderUUIDs,
          },
        });

        // Offline failures are silent, so they still need a toast here
        if (!(result.status === 'failed' && result.errorToastShown)) {
          showErrorToast();
        }

        setContinueButtonLoading(false);
        return;
      }

      addSmartOrdersToSubmitQueue(orderUUIDs);
      stageContinuePress(continuePressedAt);

      if (isSmallLayout) {
        navigate(ORDER_INBOUNDS_ROUTES.Submit, {
          params: {
            initialQuantityMap: quantitiesToUndo,
            initialSubstitution: undefined,
            onSubmitSmartOrder: () => selectNextSmartOrder(orderUUIDs[0]),
            smartOrderUUID: orderUUIDs[0],
          },
          screen: NEW_ORDER_ROUTES.OrderSummary,
        });
      } else {
        setDrawerState({
          initialQuantityMap: quantitiesToUndo,
          onSubmitSmartOrder: () => selectNextSmartOrder(orderUUIDs[0]),
          smartOrderUUID: orderUUIDs[0],
        });
        openDrawer();
      }
    } catch {
      showErrorToast();
    }
    setContinueButtonLoading(false);
  };

  const onUpdateMatchesPress = async () => {
    if (!reloadIfInvalidOverrideRestaurant()) {
      return;
    }
    if (!activeRestaurant || orderUUIDs.length === 0) {
      return;
    }

    try {
      await memorizeOrder({
        variables: {
          orderUUID: orderUUIDs[0],
          restaurantUUID: activeRestaurant.uuid,
          supplierUUID: activeRestaurant.supplierUUID,
        },
      });

      showToast({
        content: i18n.t('toast.order_memorized'),
        type: Type.POSITIVE,
      });

      if (isSmallLayout || isSmallTabletLayout) {
        goBack();
      } else {
        selectNextSmartOrder(orderUUIDs[0]);
      }
    } catch {
      showErrorToast();
    }
  };

  const locale = useItemQueryLocale();
  const { supplierDynamicConfigMap } = useUserContext();

  const onSelectLocation = async (
    restaurantRelationship?: EmployeeRestaurant,
  ) => {
    setUpdatingLocation(false);
    try {
      const isSameRestaurant =
        restaurantRelationship &&
        restaurantRelationship.uuid === activeRestaurant?.uuid;

      if (
        restaurantRelationship &&
        !isSameRestaurant &&
        regenerateSmartOrders
      ) {
        const supplierUUID = restaurantRelationship.supplierUUID;
        const supplierDynamicConfig = supplierDynamicConfigMap[supplierUUID];
        const isSaltApiEnabled = getValueFromConfigs(
          'salt_api_integration_enabled',
          supplierDynamicConfig,
          restaurantRelationship.chatDynamicConfig,
          defaultDynamicConfigValues.salt_api_integration_enabled,
        );
        const isItemGroupsEnabled = getValueFromConfigs(
          'item_groups_enabled',
          supplierDynamicConfig,
          restaurantRelationship.chatDynamicConfig,
          defaultDynamicConfigValues.item_groups_enabled,
        );

        // For legacy flow, loading state is handled in the wrapper,
        // but for new flow, we await the promise here.
        await regenerateSmartOrders(
          {
            employeeRestaurant: restaurantRelationship,
            locale,
            skipGroupMemberships: !isItemGroupsEnabled,
            skipSaltApi: !isSaltApiEnabled,
            smartOrderUUIDs: orderUUIDs,
          },
          RefetchQueryType.SUPPLIER,
        );
        onRegenerateSmartOrder?.(orderUUIDs[0], restaurantRelationship.uuid);
      }
    } catch (error) {
      Analytics.track('regenerate_order_inbounds.error', { error });
      showErrorToast();
    }
  };

  const updateProfile = async (
    contactInfo?: RestaurantEmployeeFragment,
    forceUpdate = false,
    overrideAssigningRestaurant:
      | EmployeeRestaurant
      | undefined = assigningRestaurant,
  ) => {
    if (!overrideAssigningRestaurant || !contactInfo) {
      return;
    }
    const arePhoneNumbersEqual =
      phoneNumber &&
      isNotMaliPhoneNumber(contactInfo.employee_contact_phone_number) &&
      contactInfo.employee_contact_phone_number === phoneNumber;
    // We default to using phone number, unless it is NOT available,
    // to help ensure we overwrite Mali phone numbers.
    const areEmailsEqual =
      !phoneNumber &&
      emailAddress &&
      contactInfo.employee_login_email === emailAddress;

    // Skip updating the employee if this is an existing employee that we're just
    // assigning a previously "UNKNOWN_CONTACT" order to that employee.
    if (!forceUpdate && (arePhoneNumbersEqual || areEmailsEqual)) {
      await assignOrderToRestaurant(
        contactInfo.employee_id,
        overrideAssigningRestaurant,
      );
      return;
    }

    try {
      Analytics.track('order_inbound_detail.update_employee_contact', {
        contactInfo,
        employeeUUID: contactInfo.employee_id,
      });

      const email = contactInfo.employee_contact_email ?? emailAddress;
      const loginEmail = emailAddress ?? contactInfo.employee_login_email;
      const response = await updateEmployee({
        variables: {
          email: email?.trim() || null,
          employeeUUID: contactInfo.employee_id,
          loginEmail: loginEmail?.trim() || null,
          phoneNumber:
            phoneNumber ?? contactInfo.employee_contact_phone_number ?? null,
          supplierUUID: overrideAssigningRestaurant.supplierUUID,
        },
      });

      const updatedEmployee = response.data?.updateEmployee.employee;
      if (updatedEmployee) {
        await assignOrderToRestaurant(
          updatedEmployee.uuid,
          overrideAssigningRestaurant,
        );
      }
    } catch (e) {
      if (e instanceof ApolloError && getMessageFromGQLError(e)) {
        showToast({
          autoCloseTimeout: 0,
          content: getMessageFromGQLError(e),
          type: Type.NEGATIVE,
        });
      } else {
        showErrorToast();
      }
    }
  };

  const assignOrderToRestaurant = async (
    employeeUUID: string,
    overrideAssigningRestaurant:
      | EmployeeRestaurant
      | undefined = assigningRestaurant,
    skipLoadingState = false,
  ) => {
    if (!overrideAssigningRestaurant) {
      return;
    }
    // `employeeId` could be an empty string
    if (employeeUUID) {
      Analytics.track('order_inbound_detail.reassign_account', {
        restaurantUUID: overrideAssigningRestaurant.uuid,
        smartOrderUUIDs: orderUUIDs,
      });
      // For legacy flow, loading state is handled in the wrapper,
      // but for new flow, we await the promise here.
      try {
        await assignSmartOrder({
          employeeRestaurant: overrideAssigningRestaurant,
          employeeUUID,
          skipLoadingState,
        });
      } catch (e) {
        showErrorToast();
        Analytics.track('assign_smart_order.error', {
          employeeId: null,
          error:
            e instanceof ApolloError
              ? getMessageFromGQLError(e)
              : 'Unknown error',
          restaurantId: overrideAssigningRestaurant.uuid,
          smartOrderId: orderUUID,
        });
      }
    } else {
      showErrorToast();
      Analytics.track('assign_smart_order.error', {
        employeeId: null,
        error: 'No employeeId returned on complete.',
        restaurantId: overrideAssigningRestaurant.uuid,
        smartOrderId: orderUUID,
      });
    }
  };

  const onUnknownContactSave = async (unknownContactInfo: {
    contactInfo?: RestaurantEmployeeFragment | null;
    employeeUUID?: string;
    overrideAssigningRestaurant: EmployeeRestaurant;
    skipLoadingState?: boolean;
  }) => {
    const {
      overrideAssigningRestaurant,
      contactInfo,
      employeeUUID,
      skipLoadingState,
    } = unknownContactInfo;
    if (!overrideAssigningRestaurant) {
      return;
    }
    if (contactInfo) {
      // Force update because one contact info (email or phone number) has changed
      await updateProfile(contactInfo, true, overrideAssigningRestaurant);
      return;
    }
    if (employeeUUID) {
      await assignOrderToRestaurant(
        employeeUUID,
        overrideAssigningRestaurant,
        skipLoadingState,
      );
    }
  };

  const showLocationPicker = () => !isViewOnly && setUpdatingLocation(true);

  return {
    addItemSmartOrderUUID,
    continueButtonLoading,
    detailListItems,
    dispatch,
    getAddToOrderDisabled,
    isMemorizingOrder,
    navigateToAdd,
    onContinuePress,
    onItemCorrection,
    onRemoveItem,
    onSelectLocation,
    onSetRememberSelection,
    onSwapItem,
    onUnknownContactSave,
    onUpdateMatchesPress,
    orderInboundState,
    orderUUIDs,
    pillNavProps,
    recordInteraction,
    setAssigningRestaurant,
    sheetRef,
    showLocationPicker,
  };
};
